// Polygon vertex-edit hook. Takes an existing closed ring, lets the user drag
// vertices, and emits the new ring. Pair this with two map sources: one for the
// polygon itself and one for the vertex handles.
import { useCallback, useEffect, useRef, useState } from 'react';
export function usePolygonEdit(mapRef, opts) {
    const layerId = opts.layerId ?? 'expansion-edit-verts';
    const [ring, setRing] = useState([]);
    const ringRef = useRef([]);
    const draggingIdxRef = useRef(null);
    const onChangeRef = useRef(opts.onChange);
    onChangeRef.current = opts.onChange;
    const setBoth = useCallback((next) => {
        ringRef.current = next;
        setRing(next);
        onChangeRef.current?.(next);
    }, []);
    // Initialize ring when activated OR when the initial ring reference
    // actually changes (e.g. user switched active zone while editMode was on).
    // We compute a cheap signature of the ring so array-identity churn from
    // parent re-renders doesn't thrash this effect.
    const initialRingSig = opts.initialRing.length === 0
        ? ''
        : `${opts.initialRing.length}:${opts.initialRing[0][0].toFixed(5)},${opts.initialRing[0][1].toFixed(5)}|${opts.initialRing[opts.initialRing.length - 1][0].toFixed(5)},${opts.initialRing[opts.initialRing.length - 1][1].toFixed(5)}`;
    useEffect(() => {
        if (opts.active && opts.initialRing.length) {
            setBoth([...opts.initialRing]);
        }
        else if (!opts.active) {
            setBoth([]);
        }
    }, [opts.active, initialRingSig]); // eslint-disable-line react-hooks/exhaustive-deps
    // Mouse handlers on the map
    useEffect(() => {
        if (!opts.active)
            return;
        const map = mapRef.current;
        if (!map)
            return;
        const onMouseDown = (e) => {
            const f = (e.features ?? [])[0];
            if (!f)
                return;
            const idx = f.properties?.idx;
            if (typeof idx !== 'number')
                return;
            e.preventDefault();
            draggingIdxRef.current = idx;
            map.getCanvas().style.cursor = 'grabbing';
            map.dragPan.disable();
        };
        const onMouseMove = (e) => {
            const idx = draggingIdxRef.current;
            if (idx === null)
                return;
            const ll = [e.lngLat.lng, e.lngLat.lat];
            const r = [...ringRef.current];
            r[idx] = ll;
            // Keep closure: if the ring was closed (first == last) and we moved the
            // first vertex, also move the last.
            if (r.length > 1 && idx === 0) {
                const last = r.length - 1;
                r[last] = ll;
            }
            setBoth(r);
        };
        const onMouseUp = () => {
            draggingIdxRef.current = null;
            map.getCanvas().style.cursor = 'crosshair';
            map.dragPan.enable();
        };
        // Hover cursor hint
        const onMouseEnterVert = () => { map.getCanvas().style.cursor = 'grab'; };
        const onMouseLeaveVert = () => {
            if (draggingIdxRef.current === null)
                map.getCanvas().style.cursor = 'crosshair';
        };
        // Listen for mouseup on the WINDOW, not the map canvas — otherwise if the
        // user releases the button outside the map (on the sidebar, the browser
        // chrome, or anywhere else), the drag never ends and the vertex follows
        // the cursor until they click back on the map. Same for mousemove: we
        // need movement tracked even when the cursor leaves the canvas briefly.
        const onWindowMouseUp = () => onMouseUp();
        const onWindowMouseMove = (ev) => {
            if (draggingIdxRef.current === null)
                return;
            const rect = map.getCanvas().getBoundingClientRect();
            const x = ev.clientX - rect.left;
            const y = ev.clientY - rect.top;
            const lngLat = map.unproject([x, y]);
            onMouseMove({ lngLat });
        };
        map.on('mousedown', layerId, onMouseDown);
        map.on('mousemove', onMouseMove); // still catch canvas-interior moves for feature-layer events
        window.addEventListener('mousemove', onWindowMouseMove);
        window.addEventListener('mouseup', onWindowMouseUp);
        map.on('mouseenter', layerId, onMouseEnterVert);
        map.on('mouseleave', layerId, onMouseLeaveVert);
        map.getCanvas().style.cursor = 'crosshair';
        return () => {
            map.off('mousedown', layerId, onMouseDown);
            map.off('mousemove', onMouseMove);
            window.removeEventListener('mousemove', onWindowMouseMove);
            window.removeEventListener('mouseup', onWindowMouseUp);
            map.off('mouseenter', layerId, onMouseEnterVert);
            map.off('mouseleave', layerId, onMouseLeaveVert);
            map.getCanvas().style.cursor = '';
            map.dragPan.enable();
        };
    }, [opts.active, mapRef, setBoth]);
    const reset = useCallback(() => {
        if (opts.initialRing.length)
            setBoth([...opts.initialRing]);
    }, [opts.initialRing, setBoth]);
    return { ring, reset };
}
